import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.name}`);
      const target = u.role === "admin" ? "/admin" : u.role === "franchise" ? "/franchise" : "/dashboard";
      nav(loc.state?.from || target);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  const handleForgot = (e) => {
    e.preventDefault();
    toast.info(
      "Please contact your sponsor or write to support@samarawiz.com from your registered email to reset your password.",
      { duration: 6000 }
    );
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-white">
      {/* Left brand panel */}
      <div className="relative hidden md:block">
        <img
          src="https://images.pexels.com/photos/10827366/pexels-photo-10827366.jpeg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-navy/90 to-navy/70" />
        <div className="relative h-full p-12 flex flex-col justify-between text-white">
          <Logo light />
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-gold">Welcome back</div>
            <h2 className="font-serif text-5xl mt-4 leading-tight">Your<br/>wealth journey<br/>continues.</h2>
            <p className="mt-6 text-white/70 max-w-md text-sm leading-relaxed">
              Log in with your Samara Wiz IBA / Customer credentials. New here? Register to unlock your dashboard,
              retail earnings and rank progress.
            </p>
          </div>
          <div className="text-xs text-white/40">SAMARA · Health · Education · Beauty</div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8 flex justify-center"><Logo /></div>

          <div className="text-xs uppercase tracking-[0.4em] text-gold">Sign in</div>
          <h1 className="font-serif text-4xl text-navy mt-2">Welcome back</h1>
          <p className="text-sm text-navy/60 mt-1">Enter your registered credentials to continue.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" autoComplete="on">
            <div>
              <label htmlFor="login-email" className="text-[11px] uppercase tracking-widest text-navy/60">
                Email or User ID
              </label>
              <input
                id="login-email"
                data-testid="login-email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com or SM100001"
                className="mt-1 w-full border border-border px-3 py-3 text-sm rounded-sm focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/20 transition"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="login-password" className="text-[11px] uppercase tracking-widest text-navy/60">
                  Password
                </label>
                <button
                  type="button"
                  onClick={handleForgot}
                  data-testid="login-forgot-password"
                  className="text-[11px] text-gold hover:text-gold-deep hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative mt-1">
                <input
                  id="login-password"
                  data-testid="login-password"
                  required
                  autoComplete="current-password"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full border border-border px-3 py-3 pr-10 text-sm rounded-sm focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/20 transition"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  aria-label={show ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-navy/50 hover:text-navy"
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              data-testid="login-submit"
              disabled={busy}
              className="w-full bg-navy text-white py-3 hover:bg-navy-deep rounded-sm disabled:opacity-50 transition-colors text-sm tracking-wider"
            >
              {busy ? "Signing in…" : "Login"}
            </button>
          </form>

          <p className="mt-8 text-sm text-navy/60 text-center">
            Don&apos;t have an account?{" "}
            <Link to="/register" data-testid="login-register-link" className="text-gold font-medium hover:text-gold-deep">
              Register →
            </Link>
          </p>

          <div className="mt-8 flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest text-navy/40">
            <ShieldCheck size={12} className="text-olive" />
            Secured by Samara Wiz Pvt. Ltd.
          </div>
        </div>
      </div>
    </div>
  );
}
